// FeatureCard — card unificata della dashboard.
//
// Risposta a feedback utente 2026-05-23: prima ogni card aveva un colore
// diverso (blu/ambra/verde/rosa/slate) in base allo stato → confusione
// e nessuna gerarchia. Adesso:
//
//   1. STATO ATTIVO (count > 0) → bordo BLU singolo (palette.accent),
//      conteggio + lista record.
//   2. STATO INATTIVO (count === 0, intro dismissata) → grigio come la
//      card del Manuale HACCP, frase compatta "Nessun record".
//   3. EMPTY STATE INTRO (count === 0, intro NON ancora dismissata) →
//      blocco grigio descrittivo con: titolo grande, descrizione di
//      cosa fa la feature, bottone "Inizia subito" (dismiss persistente
//      su localStorage per user+business+feature_key), link "Chiedi al
//      consulente" (apre WhatsApp).
//
// La persistenza dello stato "intro dismissata" è scoped a
// user+business+featureKey — se cambi locale, la prima volta vedi di
// nuovo l'intro per quella feature su quel locale. Questo evita che
// un utente che ha già imparato la feature su un locale si trovi
// l'intro davanti su un altro, ma garantisce che ogni nuovo cliente
// che apre l'app per la prima volta veda le spiegazioni.

import { useEffect, useState, type ReactNode } from 'react';
import { palette } from './design.js';

const WHATSAPP_CONSULENTE = 'https://wa.me/393486382343';

function introStorageKey(featureKey: string, userId?: string | null, businessId?: string | null): string {
  return `trackeat.intro_dismissed.${userId ?? 'anon'}.${businessId ?? 'none'}.${featureKey}`;
}

export function isIntroDismissed(featureKey: string, userId?: string | null, businessId?: string | null): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return localStorage.getItem(introStorageKey(featureKey, userId, businessId)) === '1';
  } catch {
    return false;
  }
}

export function dismissIntro(featureKey: string, userId?: string | null, businessId?: string | null): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.setItem(introStorageKey(featureKey, userId, businessId), '1');
  } catch { /* noop: storage pieno o disabilitato */ }
}

// ─── FeatureIntroBlock ─────────────────────────────────────────────
// Componente STANDALONE riusabile dentro le pagine vuote (non solo in
// dashboard). Quando una pagina feature (Pulizie, Temperature,
// Scadenze, ecc.) non ha record, mostriamo questo blocco invece
// del generico "Nessun elemento": descrizione di cosa fa la feature,
// CTA "Inizia subito" che dismissa la spiegazione + Chiedi al
// consulente via WhatsApp.

export interface FeatureIntroBlockProps {
  featureKey: string;
  icon: string;
  title: string;
  description: ReactNode;
  whatsappQuestion?: string;
  /** Etichetta CTA "Inizia subito" customizzabile. */
  startLabel?: string;
  onStart: () => void;
  userId?: string | null;
  businessId?: string | null;
  /** Quando renderizzato in una pagina con padding proprio, possiamo
   *  togliere il margin esterno. */
  flush?: boolean;
}

export function FeatureIntroBlock(props: FeatureIntroBlockProps): JSX.Element {
  const {
    featureKey, icon, title, description, whatsappQuestion,
    startLabel, onStart, userId, businessId, flush,
  } = props;
  const waUrl = whatsappQuestion
    ? `${WHATSAPP_CONSULENTE}?text=${encodeURIComponent(whatsappQuestion)}`
    : WHATSAPP_CONSULENTE;
  return (
    <div
      style={{
        background: '#f8fafc',
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        padding: 18,
        margin: flush ? 0 : '12px 0',
        boxShadow: '0 1px 2px rgba(15,23,42,.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <span style={{
          width: 44, height: 44, borderRadius: 12,
          background: palette.card, border: `1px solid ${palette.border}`,
          display: 'grid', placeItems: 'center',
          fontSize: 24, flexShrink: 0,
        }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: palette.text, lineHeight: 1.3 }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: palette.textFaint, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginTop: 2 }}>
            Funzionalità da iniziare
          </div>
        </div>
      </div>
      <div style={{
        fontSize: 13, color: palette.textMuted,
        lineHeight: 1.55, marginBottom: 16,
      }}>
        {description}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => {
            dismissIntro(featureKey, userId, businessId);
            onStart();
          }}
          style={{
            flex: 1, minWidth: 140,
            padding: '11px 16px', borderRadius: 10,
            background: palette.accent, color: '#fff',
            border: 'none', fontSize: 14, fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,102,204,.18), 0 2px 6px rgba(0,102,204,.12)',
          }}
        >
          {startLabel ?? 'Inizia subito'}
        </button>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1, minWidth: 140,
            padding: '11px 16px', borderRadius: 10,
            background: 'transparent',
            border: `1px solid ${palette.border}`,
            color: palette.textMuted, fontSize: 13, fontWeight: 600,
            textAlign: 'center', textDecoration: 'none',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          💬 Chiedi al consulente
        </a>
      </div>
    </div>
  );
}

export interface FeatureCardProps {
  /** Identificatore unico della feature. Usato come chiave per persistere
   *  il dismiss dell'intro. */
  featureKey: string;
  /** Emoji/icona della feature (es. "🌡️", "🧽"). */
  icon: string;
  /** Titolo breve della feature (es. "Temperature"). */
  title: string;
  /** Numero di record correnti per questa feature. Se 0 → empty state. */
  count: number;
  /** Label del conteggio, plurale-aware (es. "frigo rilevato oggi" /
   *  "frigo rilevati oggi"). Usato solo se count > 0. */
  countLabel: string;
  /** Descrizione lunga della feature, mostrata nell'empty state intro.
   *  Spiega COSA fa la feature e PERCHÉ è utile.
   *  Markdown-free, plain text. */
  description: ReactNode;
  /** Pre-compilato del messaggio WhatsApp al consulente. */
  whatsappQuestion?: string;
  /** Callback al click su card / "Inizia subito". Tipicamente naviga
   *  alla pagina della feature. */
  onStart: () => void;
  /** Contenuto interno della card quando count > 0 (es. lista record). */
  children?: ReactNode;
  /** Contesto persistenza: id utente + id locale. */
  userId?: string | null;
  businessId?: string | null;
}

export function FeatureCard(props: FeatureCardProps): JSX.Element {
  const {
    featureKey, icon, title, count, countLabel,
    description, whatsappQuestion, onStart, children,
    userId, businessId,
  } = props;

  const [dismissed, setDismissed] = useState<boolean>(() =>
    isIntroDismissed(featureKey, userId, businessId),
  );
  // Re-read al cambio business (se l'utente switcha tenant/locale).
  useEffect(() => {
    setDismissed(isIntroDismissed(featureKey, userId, businessId));
  }, [featureKey, userId, businessId]);

  const isEmpty = count <= 0;
  const showIntro = isEmpty && !dismissed;

  // Wrapper UNICO per le 3 varianti — l'utente percepisce SEMPRE la stessa
  // card, con header coerente. Cambia solo il body. Bordo a sinistra blu
  // se attiva, grigio se vuota.
  const accent = isEmpty ? palette.borderStrong : palette.accent;
  const waUrl = whatsappQuestion
    ? `${WHATSAPP_CONSULENTE}?text=${encodeURIComponent(whatsappQuestion)}`
    : WHATSAPP_CONSULENTE;

  return (
    <div
      role={showIntro ? undefined : 'button'}
      tabIndex={showIntro ? undefined : 0}
      onClick={showIntro ? undefined : onStart}
      onKeyDown={
        showIntro
          ? undefined
          : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStart(); } }
      }
      style={{
        background: palette.card,
        borderRadius: 14,
        borderLeft: `4px solid ${accent}`,
        boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05), 0 1px 1px rgba(15,23,42,.03)',
        padding: '14px 16px',
        margin: '8px 0',
        cursor: showIntro ? 'default' : 'pointer',
        position: 'relative',
      }}
    >
      {/* Header CARD: icona + titolo (+ count se attiva) — sempre uguale */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: showIntro || !isEmpty ? 8 : 0,
      }}>
        <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: palette.text }}>{title}</div>
        </div>
        {!isEmpty && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: palette.accent }}>{count}</span>
          </div>
        )}
      </div>

      {/* Body: 3 varianti */}
      {!isEmpty && (
        <>
          <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 6 }}>
            {countLabel}
          </div>
          {children}
        </>
      )}
      {showIntro && (
        <>
          <div style={{
            fontSize: 13, color: palette.textMuted,
            lineHeight: 1.55, marginBottom: 12,
          }}>
            {description}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                dismissIntro(featureKey, userId, businessId);
                setDismissed(true);
                onStart();
              }}
              style={{
                flex: 1, minWidth: 130,
                padding: '10px 14px', borderRadius: 10,
                background: palette.accent, color: '#fff',
                border: 'none', fontSize: 14, fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,102,204,.18), 0 2px 6px rgba(0,102,204,.12)',
              }}
            >
              Inizia subito
            </button>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1, minWidth: 130,
                padding: '10px 14px', borderRadius: 10,
                background: 'transparent',
                border: `1px solid ${palette.border}`,
                color: palette.textMuted, fontSize: 13, fontWeight: 600,
                textAlign: 'center', textDecoration: 'none',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              💬 Chiedi al consulente
            </a>
          </div>
        </>
      )}
      {isEmpty && !showIntro && (
        <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 4, fontStyle: 'italic' }}>
          Nessun record · tap per iniziare
        </div>
      )}
    </div>
  );
}
